import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Send, RefreshCw, Plus, Trash2, MessageCircle } from "lucide-react";
import {
    adminTestTelegram,
    adminGetTelegramRecipients,
    adminSaveTelegramRecipients,
    adminDiscoverTelegram,
} from "@/lib/api";

/**
 * Manage which Telegram chats receive lead alerts.
 * - Lists currently-linked recipients with their display name + remove button
 * - "Discover" button calls /getUpdates and shows every chat that has messaged
 *   the bot recently, with a one-click Add button per row
 * - "Send test" button pings every linked recipient
 */
export default function TelegramRecipientsCard() {
    const [recipients, setRecipients] = useState([]);
    const [discoverable, setDiscoverable] = useState(null);
    const [loading, setLoading] = useState(false);
    const [working, setWorking] = useState(false);
    const [discovering, setDiscovering] = useState(false);
    const [testing, setTesting] = useState(false);

    const refresh = useCallback(async () => {
        setLoading(true);
        try {
            const r = await adminGetTelegramRecipients();
            setRecipients(r.recipients || []);
        } catch {
            toast.error("Could not load Telegram recipients");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        refresh();
    }, [refresh]);

    const saveIds = async (ids) => {
        setWorking(true);
        try {
            const r = await adminSaveTelegramRecipients(ids);
            toast.success("Recipients updated.");
            // refresh canonical list (with profile names) from server
            await refresh();
            // also refresh the discoverable list so "linked" badges update
            if (discoverable) await discover(false);
            return r;
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed to save");
        } finally {
            setWorking(false);
        }
    };

    const removeId = (chatId) => {
        if (!window.confirm(`Stop sending lead alerts to ${chatId}?`)) return;
        const next = recipients
            .filter((r) => r.chat_id !== chatId)
            .map((r) => r.chat_id);
        saveIds(next);
    };

    const addId = (chatId) => {
        const next = Array.from(new Set([...recipients.map((r) => r.chat_id), chatId]));
        saveIds(next);
    };

    const discover = async (showToast = true) => {
        setDiscovering(true);
        try {
            const r = await adminDiscoverTelegram();
            setDiscoverable(r.chats || []);
            if (showToast) {
                if (!r.chats?.length) {
                    toast(
                        "No chats found in the last 24h — ask the recipient to message @Acumenfs_leads_bot, then click Discover again.",
                        { duration: 8000 }
                    );
                } else {
                    toast.success(`Found ${r.chats.length} chat${r.chats.length === 1 ? "" : "s"}.`);
                }
            }
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Discover failed");
        } finally {
            setDiscovering(false);
        }
    };

    const test = async () => {
        setTesting(true);
        try {
            const r = await adminTestTelegram();
            const sent = r.sent_to?.length || 0;
            const failed = r.failed?.length || 0;
            if (sent > 0) {
                toast.success(
                    `Test sent to ${sent} recipient${sent === 1 ? "" : "s"}.` +
                        (failed > 0 ? ` (${failed} failed — check Telegram is allowed.)` : "")
                );
            } else {
                toast.error("Test could not be delivered. Check linked chats.");
            }
        } catch (err) {
            toast.error(
                err?.response?.data?.detail ||
                    "Test failed. Make sure the recipient has messaged @Acumenfs_leads_bot first."
            );
        } finally {
            setTesting(false);
        }
    };

    return (
        <section
            data-testid="telegram-card"
            className="bg-brand-surface border border-brand-border p-6 space-y-5"
        >
            <div>
                <h2 className="font-bold text-brand-primary flex items-center gap-2">
                    <MessageCircle size={16} /> Telegram lead alerts
                </h2>
                <p className="text-xs text-brand-muted mt-1 max-w-2xl leading-relaxed">
                    Every new lead is pinged to <strong className="text-brand-primary">@Acumenfs_leads_bot</strong>{" "}
                    in real time. Anyone you want to receive alerts just needs to open
                    Telegram, find the bot and tap <strong>Start</strong> — then click{" "}
                    <strong>Discover linked chats</strong> below and add them.
                </p>
            </div>

            {/* Linked recipients */}
            <div>
                <div className="text-[11px] uppercase tracking-[0.2em] text-brand-muted font-bold mb-2">
                    Currently receiving alerts ({recipients.length})
                </div>
                {loading ? (
                    <div className="text-sm text-brand-muted">Loading…</div>
                ) : recipients.length === 0 ? (
                    <div className="text-sm text-brand-muted bg-brand-bg/50 border border-dashed border-brand-border rounded p-4">
                        No recipients linked yet. Use{" "}
                        <strong>Discover linked chats</strong> to add one.
                    </div>
                ) : (
                    <ul data-testid="telegram-recipients-list" className="space-y-2">
                        {recipients.map((r) => (
                            <li
                                key={r.chat_id}
                                data-testid={`telegram-recipient-${r.chat_id}`}
                                className="flex items-center justify-between gap-3 bg-brand-bg/50 border border-brand-border rounded px-4 py-3"
                            >
                                <div className="min-w-0">
                                    <div className="font-bold text-sm text-brand-primary truncate">
                                        {r.title}
                                        {r.username ? (
                                            <span className="text-brand-muted font-normal ml-1">
                                                · @{r.username}
                                            </span>
                                        ) : null}
                                    </div>
                                    <div className="text-[11px] font-mono text-brand-muted">
                                        {r.chat_id}
                                        {r.type ? ` · ${r.type}` : ""}
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => removeId(r.chat_id)}
                                    disabled={working}
                                    data-testid={`telegram-remove-${r.chat_id}`}
                                    className="inline-flex items-center gap-1 text-xs text-[#B33B3B] font-bold hover:underline disabled:opacity-60"
                                    title="Remove this recipient"
                                >
                                    <Trash2 size={13} /> Remove
                                </button>
                            </li>
                        ))}
                    </ul>
                )}
            </div>

            {/* Actions */}
            <div className="flex flex-wrap gap-3 pt-2">
                <button
                    type="button"
                    onClick={() => discover()}
                    disabled={discovering}
                    data-testid="telegram-discover"
                    className="inline-flex items-center gap-2 bg-[#229ED9] text-white px-5 py-3 text-sm font-bold tracking-wide hover:bg-[#1A82B5] transition-colors disabled:opacity-60 rounded"
                >
                    <RefreshCw size={14} className={discovering ? "animate-spin" : ""} />
                    {discovering ? "Discovering…" : "Discover linked chats"}
                </button>
                <button
                    type="button"
                    onClick={test}
                    disabled={testing || recipients.length === 0}
                    data-testid="telegram-test"
                    className="inline-flex items-center gap-2 bg-brand-primary text-white px-5 py-3 text-sm font-bold tracking-wide hover:bg-brand-primaryHover transition-colors disabled:opacity-60 rounded"
                >
                    <Send size={14} />
                    {testing ? "Sending…" : "Send test message"}
                </button>
            </div>

            {/* Discovered list */}
            {discoverable && discoverable.length > 0 && (
                <div className="pt-4 border-t border-brand-border">
                    <div className="text-[11px] uppercase tracking-[0.2em] text-brand-muted font-bold mb-2">
                        Recently messaged the bot ({discoverable.length})
                    </div>
                    <ul data-testid="telegram-discoverable" className="space-y-2">
                        {discoverable.map((c) => (
                            <li
                                key={c.chat_id}
                                className={`flex items-center justify-between gap-3 border rounded px-4 py-3 ${
                                    c.linked
                                        ? "bg-brand-primary/5 border-brand-primary/30"
                                        : "bg-brand-surfaceAlt border-brand-border"
                                }`}
                            >
                                <div className="min-w-0">
                                    <div className="font-bold text-sm text-brand-primary truncate">
                                        {c.title}
                                        {c.username ? (
                                            <span className="text-brand-muted font-normal ml-1">
                                                · @{c.username}
                                            </span>
                                        ) : null}
                                    </div>
                                    <div className="text-[11px] font-mono text-brand-muted">
                                        {c.chat_id}
                                        {c.type ? ` · ${c.type}` : ""}
                                    </div>
                                </div>
                                {c.linked ? (
                                    <span className="text-[11px] uppercase tracking-[0.18em] font-bold text-brand-primary">
                                        ✓ Linked
                                    </span>
                                ) : (
                                    <button
                                        type="button"
                                        onClick={() => addId(c.chat_id)}
                                        disabled={working}
                                        data-testid={`telegram-add-${c.chat_id}`}
                                        className="inline-flex items-center gap-1 text-xs bg-brand-primary text-white px-3 py-1.5 rounded font-bold hover:bg-brand-primaryHover transition-colors disabled:opacity-60"
                                    >
                                        <Plus size={13} /> Add as recipient
                                    </button>
                                )}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </section>
    );
}
